import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import AppShell from './layout/AppShell.vue';

// Route components are lazy (vue-router accepts an async component factory as
// `component`). This code-splits each page into its own chunk so the initial
// bundle — served for Login too — no longer ships the whole designer + ECharts
// up front. The shell (AppShell) stays eager since it wraps every route.
const Home = () => import('./pages/Home.vue');
const Variables = () => import('./pages/Variables.vue');
const Agents = () => import('./pages/Agents.vue');
const AgentDetail = () => import('./pages/AgentDetail.vue');
const Screens = () => import('./pages/Screens.vue');
const Sources = () => import('./pages/Sources.vue');
const WidgetBundles = () => import('./pages/WidgetBundles.vue');
const LlmConfig = () => import('./pages/LlmConfig.vue');
const TenantSettings = () => import('./pages/TenantSettings.vue');
const Chat = () => import('./pages/Chat.vue');
const Storyboard = () => import('./pages/Storyboard.vue');
const Components = () => import('./pages/Components.vue');
const Design = () => import('./pages/Design.vue');
const Theme = () => import('./pages/Theme.vue');
const Quickstart = () => import('./pages/Quickstart.vue');
const Alerts = () => import('./pages/Alerts.vue');
const Alarms = () => import('./pages/Alarms.vue');
const Members = () => import('./pages/Members.vue');
const Organization = () => import('./pages/Organization.vue');
const AcceptInvite = () => import('./pages/AcceptInvite.vue');
const Ops = () => import('./pages/Ops.vue');
const AuditLog = () => import('./pages/AuditLog.vue');
const RequestInspect = () => import('./pages/RequestInspect.vue');
const GateDenials = () => import('./pages/GateDenials.vue');
const Billing = () => import('./pages/Billing.vue');
const Usage = () => import('./pages/Usage.vue');
const Copilot = () => import('./pages/Copilot.vue');
const KnowledgeReview = () => import('./pages/KnowledgeReview.vue');
const Automations = () => import('./pages/Automations.vue');

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: AppShell,
    children: [
      { path: '',           name: 'home',       component: Home },
      { path: 'quickstart', name: 'quickstart', component: Quickstart },
      { path: 'chat',       name: 'chat',       component: Chat },
      { path: 'explore',    redirect: '/variables' }, // Explore retired → Catalog (kept so old links don't 404)
      { path: 'variables',  name: 'variables',  component: Variables },
      { path: 'sources',     name: 'sources',     component: Sources },
      { path: 'agents',     name: 'agents',     component: Agents },
      { path: 'agents/:id', name: 'agent-detail', component: AgentDetail },
      { path: 'screens',    name: 'screens',    component: Screens },
      // Legacy dashboards page retired — designs live in the Storyboard.
      { path: 'dashboards', redirect: { name: 'storyboard' } },
      // Legacy dashboard editor retired — all designs open in the unified
      // (design) editor. Redirect keeps old /designer/:id links working.
      { path: 'designer/:id', redirect: (to) => ({ name: 'design', params: { id: to.params.id } }) },
      { path: 'storyboard', name: 'storyboard', component: Storyboard },
      { path: 'projects', name: 'projects', component: () => import('./pages/Projects.vue') },
      { path: 'projects/:id', name: 'project', component: () => import('./pages/Projects.vue') },
      { path: 'projects/:id/:section(screens|widgets|plc|mes|environments|sessions|revisions|displays|publications|installations|settings)', name: 'project-section', component: () => import('./pages/Projects.vue') },
      { path: 'projects/:id/run/:sessionId', name: 'project-player', component: () => import('./pages/ProjectPlayer.vue') },
      { path: 'components', name: 'components', component: Components },
      { path: 'component/:id', name: 'component', component: () => import('./pages/ComponentEditor.vue') },
      { path: 'design/:id', name: 'design', component: Design },
      { path: 'theme',      name: 'theme',      component: Theme },
      { path: 'widgets',    name: 'widgets',    component: WidgetBundles },
      { path: 'alerts',     name: 'alerts',     component: Alerts },
      { path: 'alarms',     name: 'alarms',     component: Alarms },
      { path: 'llm-config', name: 'llm-config', component: LlmConfig },
      { path: 'billing',    name: 'billing',    component: Billing },
      { path: 'usage',      name: 'usage',      component: Usage },
      { path: 'copilot',    name: 'copilot',    component: Copilot },
      { path: 'knowledge',  name: 'knowledge',  component: KnowledgeReview },
      { path: 'automations', name: 'automations', component: Automations },
      { path: 'settings',   name: 'settings',   component: TenantSettings },
      { path: 'members',    name: 'members',    component: Members },
      { path: 'organization', name: 'organization', component: Organization },
      { path: 'audit',      name: 'audit',      component: AuditLog },
      { path: 'inspect',    name: 'inspect',    component: RequestInspect },
      { path: 'denials',    name: 'denials',    component: GateDenials },
      // Admin-only route; registered for everyone but the sidebar hides it
      // unless auth.isAdmin, and the backend's adminOnly gate rejects the
      // ops_* tools (defence-in-depth). A non-admin who navigates here by URL
      // now sees an explicit "admin role required" banner (Ops.vue), not a
      // broken page.
      { path: 'ops',        name: 'ops',        component: Ops },
    ],
  },
  // Standalone — no AppShell. The invitee may not yet have a tenant,
  // and the sidebar's whoami lookup would surface a confusing
  // "tenant required" toast on this page.
  { path: '/accept-invite', name: 'accept-invite', component: AcceptInvite },
  // Doc 34 (F2) — tokenized read-only design for kiosks. Standalone (no
  // AppShell / session): a screen's slide iframe loads this; auth is the embed
  // token in ?token=, validated by the gateway endpoints it calls.
  { path: '/screen/design/:id', name: 'screen-design', component: () => import('./pages/EmbedDesignView.vue') },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});
